import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { ListItemRow } from 'src/components/layout/item-collection'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { TemplateBodyPreview } from './template-body-preview'
import { RecommendedTemplate } from 'src/services/whatsapp/models'

interface RecommendedTemplateCardProps {
  template: RecommendedTemplate
  onUse: (template: RecommendedTemplate) => void
  viewMode?: ViewMode
}

/**
 * A server-offered template the org hasn't created yet. Mirrors `TemplateCard`'s
 * outline + header strip, dashed to read as "not yet yours"; "Use template"
 * seeds the create form rather than submitting anything on its own.
 */
export const RecommendedTemplateCard: FC<RecommendedTemplateCardProps> = ({
  template,
  onUse,
  viewMode = 'card',
}) => {
  const { t } = useTranslation()
  const testKey = `${template.name}-${template.language}`

  const useButton = (
    <Button
      variant="secondary"
      size="sm"
      className={viewMode === 'card' ? 'mt-4 self-start' : undefined}
      onClick={() => onUse(template)}
      data-testid={`use-recommended-template-${testKey}`}
    >
      {t('whatsappTemplates.useRecommended')}
    </Button>
  )

  if (viewMode === 'list') {
    return (
      <ListItemRow
        className="border-2 border-dashed border-primary-200"
        data-testid={`recommended-template-${testKey}`}
      >
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-text-primary" title={template.name}>
            {template.name}
          </h3>
          <p className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-placeholder">
            <span className="whitespace-nowrap uppercase">{template.language}</span>
            <span className="whitespace-nowrap">{template.category}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
            <SparklesIcon className="h-3 w-3" aria-hidden="true" />
            {t('whatsappTemplates.recommendedBadge')}
          </span>
          {useButton}
        </div>
      </ListItemRow>
    )
  }

  return (
    <div
      className="flex h-full flex-col rounded-xl border-2 border-dashed border-primary-200 bg-surface shadow-sm"
      data-testid={`recommended-template-${testKey}`}
    >
      <div className="flex items-center gap-2 rounded-t-[10px] bg-primary-50 px-4 py-2 text-primary">
        <SparklesIcon className="h-4 w-4" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {t('whatsappTemplates.recommendedBadge')}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-text-primary">{template.name}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-text-secondary">
            {template.category}
          </span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs uppercase text-text-secondary">
            {template.language}
          </span>
        </div>

        <TemplateBodyPreview body={template.body} className="mt-3 flex-1" />

        {useButton}
      </div>
    </div>
  )
}
